import Image from 'next/image';
import { Link } from '@/i18n/routing';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { FavoriteButton } from '@/components/product/favorite-button';
import { calculateStorefrontPriceWithVAT, formatPriceForLocale } from '@/lib/pricing';
import { formatConsumerCategory } from '@/lib/consumer-products';
import { productPath } from '@/lib/localized-paths';

// Render-klare kaartdata; wordt zowel server-side (eerste pagina) als
// client-side (Toon meer-batches via /api/products/browse) gebruikt.
export type StorefrontProduct = {
  id: string;
  name: string;
  description: string;
  slug: string;
  category: string;
  basePrice: number;
  defaultShipping: number;
  fromQuantity?: number;
  image: string;
  canPersonalize: boolean;
};

export type StorefrontCardCopy = {
  price: string;
  vat: string;
  quantityPrefix: string;
  pieceSingular: string;
  piecePlural: string;
  perPiece: string;
  button: string;
  viewButton: string;
};

export type StorefrontCardVariant = 'default' | 'stickers' | 'boards' | 'posters' | 'canvas';

// Offerteproducten (bijvoorbeeld LED-neon) hebben bewust geen prijs; dan geen
// "EUR 0,00" op de kaart maar "op aanvraag".
const PRICE_ON_REQUEST: Record<string, string> = { nl: 'Op aanvraag', de: 'Auf Anfrage', fr: 'Sur demande', en: 'On request' };

export function StorefrontProductCard({
  product,
  locale,
  countryCode,
  copy,
  variant = 'default',
}: {
  product: StorefrontProduct;
  locale: string;
  countryCode: string;
  copy: StorefrontCardCopy;
  variant?: StorefrontCardVariant;
}) {
  const displayPrice = calculateStorefrontPriceWithVAT(product.basePrice, countryCode, product.defaultShipping);
  const quantityNote = product.fromQuantity && product.fromQuantity >= 1
    ? `${copy.quantityPrefix} ${product.fromQuantity} ${product.fromQuantity === 1 ? copy.pieceSingular : copy.piecePlural}`
    : null;

  // Products with a large minimum run would otherwise headline the whole batch:
  // chopsticks read as "from EUR 1341" when they are really EUR 0,27 apiece at
  // the 5000 minimum. Lead with the unit price and keep the minimum in the note.
  const minimumQuantity = Number(product.fromQuantity) || 1;
  const showsUnitPrice = minimumQuantity > 1 && Number.isFinite(displayPrice) && displayPrice > 0;
  const headlinePrice = showsUnitPrice ? displayPrice / minimumQuantity : displayPrice;

  const isStickerVariant = variant === 'stickers';
  const isBoardVariant = variant === 'boards';
  const isPosterVariant = variant === 'posters';
  const isCanvasVariant = variant === 'canvas';

  return (
    <Card className={`group relative h-full gap-0 overflow-hidden bg-white py-0 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${isPosterVariant || isBoardVariant ? 'rounded-none' : 'rounded-2xl'} ${isStickerVariant ? 'border-lime-200 hover:border-lime-500' : isBoardVariant ? 'border-slate-900 hover:shadow-[6px_6px_0_#ffb000]' : isPosterVariant ? 'border-slate-900 hover:shadow-[6px_6px_0_#ef3d23]' : isCanvasVariant ? 'border-stone-200 hover:border-stone-400' : 'border-slate-200'}`}>
      <FavoriteButton
        product={{ id: product.id, name: product.name, slug: product.slug, category: product.category, image: product.image }}
        className="absolute right-3 top-3 z-10"
      />
      <Link href={productPath(locale, product.slug)} className="block h-full">
        <CardHeader className="p-0">
          <div className="relative aspect-square overflow-hidden bg-white">
            {product.image ? (
              <Image
                src={product.image}
                alt={product.name}
                fill
                className="object-cover transition duration-300 group-hover:scale-105"
                sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
              />
            ) : (
              <div className={`flex h-full items-center justify-center text-5xl font-black ${isStickerVariant ? 'text-lime-300' : isBoardVariant ? 'text-amber-300' : isPosterVariant ? 'text-red-200' : isCanvasVariant ? 'text-stone-300' : 'text-rose-200'}`}>{product.name.slice(0, 2).toUpperCase()}</div>
            )}
            <div className={`absolute left-4 top-4 bg-white px-3 py-1 text-xs font-bold shadow-sm ${isPosterVariant || isBoardVariant ? 'rounded-none' : 'rounded-full'} ${isStickerVariant ? 'text-lime-800' : isBoardVariant ? 'text-slate-900' : isPosterVariant ? 'text-red-700' : isCanvasVariant ? 'text-[#6b513b]' : 'text-slate-700'}`}>
              {formatConsumerCategory(product.category, locale)}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-5">
          <h3 className={`text-xl font-black text-slate-950 ${isPosterVariant ? 'font-serif text-2xl' : ''} ${isStickerVariant ? 'group-hover:text-lime-800' : isBoardVariant ? 'group-hover:text-amber-700' : isPosterVariant ? 'group-hover:text-red-700' : isCanvasVariant ? 'group-hover:text-[#6b513b]' : 'group-hover:text-rose-700'}`}>{product.name}</h3>
          <p className="mt-2 line-clamp-2 min-h-10 text-sm leading-5 text-slate-600">{product.description}</p>
        </CardContent>
        <CardFooter className="flex items-center justify-between p-5 pt-0">
          <div>
            <div className="text-xs font-semibold text-slate-500">{copy.price}</div>
            <div className={`text-2xl font-black ${isStickerVariant ? 'text-lime-800' : isBoardVariant ? 'text-amber-700' : isPosterVariant ? 'text-red-700' : isCanvasVariant ? 'text-[#6b513b]' : 'text-rose-700'}`}>
              {headlinePrice > 0 ? formatPriceForLocale(headlinePrice, locale) : (PRICE_ON_REQUEST[locale.slice(0, 2)] || PRICE_ON_REQUEST.en)}
              {showsUnitPrice && headlinePrice > 0 ? <span className="ml-1 text-sm font-bold text-slate-500">{copy.perPiece}</span> : null}
            </div>
            <div className="mt-0.5 text-xs font-medium text-slate-500">{copy.vat}{quantityNote ? `, ${quantityNote}` : ''}</div>
          </div>
          <span className={`px-4 py-2 text-sm font-bold text-white transition ${isPosterVariant || isBoardVariant ? 'rounded-none' : 'rounded-full'} ${isStickerVariant ? 'bg-lime-700 group-hover:bg-lime-800' : isBoardVariant ? 'bg-slate-900 group-hover:bg-amber-600' : isPosterVariant ? 'bg-red-600 group-hover:bg-red-700' : isCanvasVariant ? 'bg-[#6b513b] group-hover:bg-[#503b2a]' : 'bg-blue-600 group-hover:bg-blue-700'}`}>
            {product.canPersonalize ? copy.button : copy.viewButton}
          </span>
        </CardFooter>
      </Link>
    </Card>
  );
}
